import type { FlowDefinition, FlowQuestion } from '@tutlo/contracts'

/** To samo co `FLOW_END` w contracts; zduplikowane celowo: `core` importuje z contracts tylko typy (bundle SPA bez Zoda). */
export const FLOW_END = 'plan'
/** Domyślne limity pytania `text` — zsynchronizowane z `TEXT_MIN_LENGTH`/`TEXT_MAX_LENGTH` w contracts. */
const TEXT_MIN_LENGTH = 2
const TEXT_MAX_LENGTH = 40

/** Stan kwalifikatora: czysta wartość, bez klas. Zapisywany po każdym kroku w `leads.quiz_answers`. */
export type FlowState = {
  /** `null` = kwalifikator zakończony (przechodzimy do planu). */
  readonly currentId: string | null
  readonly answers: Readonly<Record<string, string>>
  /** Kolejność odpowiedzianych pytań (do wznowienia i analityki). */
  readonly path: readonly string[]
}

const byId = (flow: FlowDefinition, id: string): FlowQuestion | undefined =>
  flow.questions.find((q) => q.id === id)

export const questionKind = (question: FlowQuestion): 'choice' | 'text' => question.kind ?? 'choice'

export function start(flow: FlowDefinition): FlowState {
  return { currentId: flow.start, answers: {}, path: [] }
}

export function isDone(state: FlowState): boolean {
  return state.currentId === null
}

export function currentQuestion(flow: FlowDefinition, state: FlowState): FlowQuestion | null {
  if (state.currentId === null) return null
  return byId(flow, state.currentId) ?? null
}

function advance(state: FlowState, question: FlowQuestion, value: string, next: string): FlowState {
  return {
    currentId: next === FLOW_END ? null : next,
    answers: { ...state.answers, [question.id]: value },
    path: [...state.path, question.id],
  }
}

/**
 * Zapisuje odpowiedź i przechodzi dalej. `choice`: `next` opcji nadpisuje `next` pytania (branching).
 * `text`: wartość przycięta, w limitach `minLength`–`maxLength` (domyślnie 2–40).
 */
export function answer(flow: FlowDefinition, state: FlowState, value: string): FlowState {
  const question = currentQuestion(flow, state)
  if (!question) throw new Error('Kwalifikator jest już zakończony')
  if (questionKind(question) === 'text') {
    const text = value.trim()
    const min = question.minLength ?? TEXT_MIN_LENGTH
    const max = question.maxLength ?? TEXT_MAX_LENGTH
    if (text.length < min || text.length > max) {
      throw new Error(`Odpowiedź na pytanie "${question.id}" musi mieć ${min}–${max} znaków`)
    }
    return advance(state, question, text, question.next ?? FLOW_END)
  }
  const option = question.options.find((o) => o.value === value)
  if (!option) throw new Error(`Nieznana opcja "${value}" dla pytania "${question.id}"`)
  return advance(state, question, value, option.next ?? question.next ?? FLOW_END)
}

/** Najdłuższa możliwa ścieżka od pytania `id` (łącznie z nim). Zabezpieczona przed cyklami. */
function longestPath(flow: FlowDefinition, id: string, seen: ReadonlySet<string>): number {
  if (seen.has(id)) return 0
  const question = byId(flow, id)
  if (!question) return 0
  const nextIds = new Set<string>([question.next ?? FLOW_END])
  for (const o of question.options) if (o.next) nextIds.add(o.next)
  const nextSeen = new Set(seen).add(id)
  let max = 0
  for (const n of nextIds) if (n !== FLOW_END) max = Math.max(max, longestPath(flow, n, nextSeen))
  return 1 + max
}

/** „Krok X z Y” — Y to górne oszacowanie po najdłuższej pozostałej gałęzi, więc pasek nigdy się nie cofa. */
export function progress(flow: FlowDefinition, state: FlowState): { step: number; total: number } {
  const answered = state.path.length
  if (state.currentId === null) return { step: answered, total: answered }
  return { step: answered + 1, total: answered + longestPath(flow, state.currentId, new Set()) }
}

/** Odtwarza stan z zapisanych odpowiedzi (wznowienie z `?step=` lub z tokenu w mailu). */
export function resume(flow: FlowDefinition, answers: Readonly<Record<string, string>>): FlowState {
  let state = start(flow)
  while (state.currentId !== null) {
    const value = answers[state.currentId]
    if (value === undefined) break
    try {
      state = answer(flow, state, value)
    } catch {
      break
    }
  }
  return state
}
